import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  TrialLockoutProvider as SharedTrialLockoutProvider,
  usePreloadImage,
  useTrialLockout,
  type TrialFeature,
} from '@discover-frontend/components';

import { isTrialUser } from 'src/utils/AuthHelperMethods';

import { usePostTrialUsageLog } from '../analytics/api/hooks';
import { TrialLockoutModal, UnlockFullVersionModal } from '../components';
import { UNLOCK_FULL_VERSION_IMAGE_URL } from '../constants/images';
import { useIsTrialUser } from './hooks/use-is-trial-user';
import { useTrialPaywallWindowEvents } from './hooks/use-trial-paywall-window-events';

export interface TrialPaywallContextValue {
  isTrial: boolean;
  isLockedOut: boolean;
  markSessionQuotaExhausted: () => void;
  openUnlockModal: (feature?: TrialFeature, category?: string) => void;
  closeUnlockModal: () => void;
  guardFeature: (
    feature: TrialFeature,
    onAllowed: () => void,
    category?: string
  ) => void;
}

const TrialPaywallContext = createContext<TrialPaywallContextValue | null>(
  null
);

interface TrialPaywallProviderProps {
  children: ReactNode;
}

const TrialPaywallProviderInner = ({ children }: TrialPaywallProviderProps) => {
  const isTrial = isTrialUser();
  const { isLockedOut, markSessionQuotaExhausted } = useTrialLockout();
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
  const [restrictedFeature, setRestrictedFeature] =
    useState<TrialFeature | null>(null);

  const trackBlockedTrialFeatureClick = usePostTrialUsageLog();

  const openUnlockModal = useCallback(
    (feature?: TrialFeature, category?: string) => {
      trackBlockedTrialFeatureClick({ feature, category });
      setRestrictedFeature(feature ?? null);
      setIsUnlockModalOpen(true);
    },
    [trackBlockedTrialFeatureClick]
  );

  const closeUnlockModal = useCallback(() => {
    setIsUnlockModalOpen(false);
    setRestrictedFeature(null);
  }, []);

  const guardFeature = useCallback(
    (feature: TrialFeature, onAllowed: () => void, category?: string) => {
      if (isLockedOut) return;
      if (isTrial) return openUnlockModal(feature, category);

      onAllowed();
    },
    [isLockedOut, isTrial, openUnlockModal]
  );

  useTrialPaywallWindowEvents({
    isTrial,
    isLockedOut,
    openUnlockModal,
    markSessionQuotaExhausted,
  });

  const value = useMemo(
    () => ({
      isTrial,
      isLockedOut,
      markSessionQuotaExhausted,
      openUnlockModal,
      closeUnlockModal,
      guardFeature,
    }),
    [
      isTrial,
      isLockedOut,
      markSessionQuotaExhausted,
      openUnlockModal,
      closeUnlockModal,
      guardFeature,
    ]
  );

  return (
    <TrialPaywallContext.Provider value={value}>
      {children}
      {!isLockedOut ? (
        <UnlockFullVersionModal
          isOpen={isUnlockModalOpen}
          feature={restrictedFeature}
          onClose={closeUnlockModal}
        />
      ) : null}
    </TrialPaywallContext.Provider>
  );
};

const TrialPaywallProviderShell = ({ children }: TrialPaywallProviderProps) => {
  // Re-renders the tree when the ID token changes; descendants read the claim live.
  const isTrial = useIsTrialUser();

  usePreloadImage({
    enabled: isTrial,
    imageUrl: UNLOCK_FULL_VERSION_IMAGE_URL,
  });

  return (
    <SharedTrialLockoutProvider
      isTrialUser={isTrialUser}
      renderLockoutModal={() => <TrialLockoutModal />}
    >
      <TrialPaywallProviderInner>{children}</TrialPaywallProviderInner>
    </SharedTrialLockoutProvider>
  );
};

export const TrialPaywallProvider = ({
  children,
}: TrialPaywallProviderProps) => (
  <TrialPaywallProviderShell>{children}</TrialPaywallProviderShell>
);

export const useTrialPaywall = (): TrialPaywallContextValue => {
  const context = useContext(TrialPaywallContext);

  if (!context) {
    throw new Error(
      'useTrialPaywall must be used within a TrialPaywallProvider'
    );
  }

  return context;
};
